import type { DataLibrarySurveyRow } from '../../types';
import { SATISFACTION_COLORS, SATISFACTION_ORDER, COACH_SATISFACTION_ORDER } from './useCoachDashboardData';

/**
 * Count occurrences of each value in a field, preserving a desired order.
 */
export function countByField(
  rows: DataLibrarySurveyRow[],
  field: 'overall_satisfaction' | 'coach_satisfaction',
  order: string[],
): Array<{ name: string; value: number; color: string }> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const val = row[field];
    if (val) counts.set(val, (counts.get(val) || 0) + 1);
  }
  return order
    .filter(name => counts.has(name))
    .map(name => ({
      name,
      value: counts.get(name)!,
      color: SATISFACTION_COLORS[name] || '#888',
    }));
}

/**
 * Build stacked bar data: one bar per coach, stacked by satisfaction value.
 */
export function buildCoachStackedBars(
  rows: DataLibrarySurveyRow[],
): Array<Record<string, string | number>> {
  // Group by coach email
  const byCoach = new Map<string, { name: string; email: string; rows: DataLibrarySurveyRow[] }>();
  for (const row of rows) {
    const email = row.coach_email || 'Unknown';
    const name = row.coach_name || email;
    if (!byCoach.has(email)) {
      byCoach.set(email, { name, email, rows: [] });
    }
    byCoach.get(email)!.rows.push(row);
  }

  // Build bar data
  return Array.from(byCoach.values())
    .sort((a, b) => b.rows.length - a.rows.length)
    .map(({ email, rows: coachRows }) => {
      const bar: Record<string, string | number> = { coach: email };
      let total = 0;
      for (const rating of SATISFACTION_ORDER) {
        const count = coachRows.filter(r => r.overall_satisfaction === rating).length;
        bar[rating] = count;
        total += count;
      }
      bar._total = total;
      return bar;
    });
}

/**
 * Build stacked bar data by cohort for a given stage and satisfaction field.
 */
export function buildCohortStageBars(
  rows: DataLibrarySurveyRow[],
  stage: 'Midpoint' | 'Endpoint',
  field: 'overall_satisfaction' | 'coach_satisfaction',
): Array<Record<string, string | number>> {
  const order = field === 'overall_satisfaction' ? SATISFACTION_ORDER : COACH_SATISFACTION_ORDER;
  const stageRows = rows.filter(r => r.survey_stage === stage);

  // Group by cohort
  const byCohort = new Map<string, DataLibrarySurveyRow[]>();
  for (const row of stageRows) {
    const cohort = row.cohort_name;
    if (!byCohort.has(cohort)) byCohort.set(cohort, []);
    byCohort.get(cohort)!.push(row);
  }

  return Array.from(byCohort.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cohort, cohortRows]) => {
      const bar: Record<string, string | number> = { cohort };
      let total = 0;
      for (const rating of order) {
        const count = cohortRows.filter(r => r[field] === rating).length;
        bar[rating] = count;
        total += count;
      }
      bar._total = total;
      return bar;
    });
}

/**
 * Get the series config for stacked bar charts (satisfaction or coach satisfaction).
 */
export function getSatisfactionSeries(field: 'overall_satisfaction' | 'coach_satisfaction') {
  const order = field === 'overall_satisfaction' ? SATISFACTION_ORDER : COACH_SATISFACTION_ORDER;
  return order.map(name => ({
    name,
    color: SATISFACTION_COLORS[name] || '#888',
  }));
}
